import type { FC } from 'react'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { windCities } from '@/constants/home'

type Point = [number, number]

/* Very rough continent outlines in longitude and latitude. Soft is the point. */
const land: Array<Array<Point>> = [
    [[-168, 65], [-140, 70], [-95, 72], [-75, 62], [-55, 52], [-66, 44], [-80, 25], [-97, 18], [-105, 22], [-117, 32], [-125, 42], [-125, 50], [-140, 59], [-165, 60]],
    [[-55, 82], [-22, 80], [-20, 70], [-44, 60], [-56, 68]],
    [[-97, 18], [-83, 9], [-77, 8], [-80, 15], [-90, 17]],
    [[-80, 10], [-60, 10], [-35, -7], [-40, -22], [-58, -38], [-68, -55], [-75, -45], [-72, -18], [-81, -5]],
    [[-10, 36], [-9, 44], [-2, 48], [5, 52], [5, 62], [12, 70], [30, 70], [40, 65], [42, 45], [28, 41], [20, 40], [10, 44], [3, 42]],
    [[-6, 50], [2, 51], [0, 55], [-3, 59], [-6, 56]],
    [[-17, 15], [-10, 30], [10, 37], [32, 31], [43, 12], [51, 11], [40, -5], [35, -25], [20, -35], [15, -25], [9, -2], [-5, 5]],
    [[42, 45], [40, 65], [60, 70], [100, 77], [140, 72], [175, 66], [160, 58], [140, 50], [130, 35], [122, 23], [108, 10], [100, 2], [92, 22], [80, 8], [72, 20], [58, 25], [50, 30], [36, 34]],
    [[36, 34], [58, 25], [55, 15], [44, 12], [35, 28]],
    [[130, 31], [141, 36], [142, 45], [137, 39]],
    [[95, 5], [120, 2], [141, -6], [110, -8]],
    [[114, -22], [122, -17], [136, -12], [146, -18], [153, -28], [146, -38], [136, -35], [115, -34]],
    [[167, -46], [178, -38], [174, -35], [168, -44]]
]

const inside = (lon: number, lat: number, polygon: Array<Point>): boolean => {
    let result = false

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i]
        const [xj, yj] = polygon[j]
        const crosses = (yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi

        if (crosses) result = !result
    }

    return result
}

const TOP = 80
const BOTTOM = -56
const HEIGHT = TOP - BOTTOM

const dots: Array<Point> = (() => {
    const result: Array<Point> = []

    for (let lat = TOP; lat >= BOTTOM; lat -= 3) {
        for (let lon = -180; lon <= 180; lon += 3) {
            if (land.some(polygon => inside(lon, lat, polygon))) result.push([lon + 180, TOP - lat])
        }
    }

    return result
})()

const toPercent = (lon: number, lat: number) => ({
    left: `${((lon + 180) / 360) * 100}%`,
    top: `${((TOP - lat) / HEIGHT) * 100}%`
})

const home = windCities[0]

export const WindMap: FC = () => {
    const [active, setActive] = useState(0)
    const city = windCities[active]

    return (
        <section
            id='wind-map'
            aria-labelledby='wind-title'
            className='px-3 md:px-5'
        >
            <div className='reg-marks relative overflow-hidden rounded-[2rem] bg-sand px-5 py-20 md:rounded-[3rem] md:px-12 md:py-28'>
                <div className='mx-auto max-w-[1300px]'>
                    <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                        <div className='lg:col-span-7'>
                            <SectionLabel index='11'>The wind map</SectionLabel>
                            <SplitText
                                as='h2'
                                id='wind-title'
                                lines={['Where the lift', 'caught on']}
                                italic='lift'
                                className='mt-5 text-[clamp(2.75rem,5.6vw,5.25rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                            />
                        </div>
                        <Reveal
                            as='p'
                            delay={150}
                            className='text-lg leading-relaxed text-stone lg:col-span-5'
                        >
                            Campaigns launched from a small studio in London that took off a long way from home. Pick a glowing dot for the story.
                        </Reveal>
                    </div>

                    <Reveal
                        delay={150}
                        className='relative mt-12'
                    >
                        <div className='relative aspect-[360/136] w-full'>
                            <svg
                                viewBox={`0 0 360 ${HEIGHT}`}
                                className='absolute inset-0 h-full w-full'
                                aria-hidden='true'
                            >
                                {dots.map(([x, y]) => (
                                    <circle
                                        key={`${x}-${y}`}
                                        cx={x}
                                        cy={y}
                                        r='0.62'
                                        fill='rgb(30 30 30 / 0.2)'
                                    />
                                ))}
                                {windCities.slice(1).map(item => {
                                    const x1 = home.lon + 180
                                    const y1 = TOP - home.lat
                                    const x2 = item.lon + 180
                                    const y2 = TOP - item.lat
                                    const lift = Math.min(40, Math.abs(x2 - x1) * 0.35 + 6)

                                    return (
                                        <path
                                            key={item.city}
                                            d={`M${x1} ${y1} Q ${(x1 + x2) / 2} ${Math.min(y1, y2) - lift} ${x2} ${y2}`}
                                            fill='none'
                                            stroke='#ff5c00'
                                            strokeOpacity={item.city === city.city ? 0.9 : 0.3}
                                            strokeWidth={item.city === city.city ? 0.7 : 0.4}
                                            className='wind-flow'
                                        />
                                    )
                                })}
                            </svg>

                            {windCities.map((item, index) => (
                                <button
                                    key={item.city}
                                    type='button'
                                    aria-pressed={active === index}
                                    aria-label={`${item.city}: ${item.brand}, ${item.result}`}
                                    onClick={() => setActive(index)}
                                    onMouseEnter={() => setActive(index)}
                                    onFocus={() => setActive(index)}
                                    className='absolute flex size-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full'
                                    style={toPercent(item.lon, item.lat)}
                                >
                                    <span className={cn('ping absolute size-3 rounded-full bg-ember', active === index ? 'opacity-100' : 'opacity-60')} />
                                    <span className={cn('relative rounded-full bg-ember shadow-[0_0_14px_4px_rgb(255_92_0/0.45)] transition-[width,height] duration-300', active === index ? 'size-3.5' : 'size-2.5')} />
                                </button>
                            ))}

                            <div
                                aria-live='polite'
                                className='pointer-events-none absolute z-10 hidden -translate-x-1/2 md:block'
                                style={{ ...toPercent(city.lon, city.lat), transform: 'translate(-50%, calc(-100% - 22px))' }}
                            >
                                <div
                                    key={city.city}
                                    className='pop-up w-60 rounded-2xl border border-ink/10 bg-paper p-4 shadow-[0_24px_40px_-24px_rgb(30_30_30/0.55)]'
                                >
                                    <p className='font-mono text-[10px] uppercase tracking-[0.16em] text-stone'>{city.city}</p>
                                    <p className='mt-1 font-heading text-xl font-bold leading-tight tracking-[-0.03em]'>{city.brand}</p>
                                    <p className='mt-1 text-sm text-ember'>{city.result}</p>
                                </div>
                            </div>
                        </div>

                        <div className='mt-6 rounded-2xl border border-ink/10 bg-paper p-4 md:hidden'>
                            <p className='font-mono text-[10px] uppercase tracking-[0.16em] text-stone'>{city.city}</p>
                            <p className='mt-1 font-heading text-xl font-bold tracking-[-0.03em]'>{city.brand}</p>
                            <p className='mt-1 text-sm text-ember'>{city.result}</p>
                        </div>
                    </Reveal>

                    <ul className='mt-10 flex flex-wrap gap-2'>
                        {windCities.map((item, index) => (
                            <li key={item.city}>
                                <button
                                    type='button'
                                    onClick={() => setActive(index)}
                                    className={cn(
                                        'rounded-full border px-3.5 py-1.5 font-mono text-xs transition-colors',
                                        active === index ? 'border-ink bg-ink text-cream' : 'border-ink/15 text-ink hover:border-ink/40'
                                    )}
                                >
                                    {item.city}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    )
}
